import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Button, Form } from 'react-bootstrap';
import '../SignUp/SignUp.css';
import './ResetPassword.css';

const ResetPassword = () => {
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        newPassword: false,
        confirmPassword: false
    });
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        text: 'Password strength',
        requirements: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false
        }
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Check password strength for new password
        if (name === 'newPassword') {
            checkPasswordStrength(value);
        }
    };

    const togglePassword = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const checkPasswordStrength = (password) => {
        const requirements = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        const validCount = Object.values(requirements).filter(Boolean).length;

        let score = 0;
        let text = 'Password strength';
        let strengthClass = '';

        if (validCount <= 2) {
            score = 33;
            text = 'Weak';
            strengthClass = 'strength-weak';
        } else if (validCount <= 4) {
            score = 66;
            text = 'Medium';
            strengthClass = 'strength-medium';
        } else {
            score = 100;
            text = 'Strong';
            strengthClass = 'strength-strong';
        }

        setPasswordStrength({
            score,
            text,
            strengthClass,
            requirements
        });

        return validCount === 5;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.newPassword || !formData.confirmPassword) {
            alert('Please fill in all fields!');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            alert('Passwords do not match!');
            return;
        }

        if (!checkPasswordStrength(formData.newPassword)) {
            alert('Please meet all password requirements!');
            return;
        }

        // Here you would typically send the reset request to your backend
        console.log('Password reset:', formData);
        alert('Password reset successfully!');
    };

    return (
        <div className="common-container d-flex justify-content-center align-items-center p-3 overflow-hidden fw-regular">
            <div className="common-card overflow-hidden my-0 mx-auto d-flex flex-column">
                <div className="common-header text-center position-relative">
                    <div className="logo mb-1 fw-bold">
                        <i className="fas fa-rocket" style={{ marginRight: '0.5rem' }}></i>
                        ProjectFlow
                    </div>
                    <div className="logo-subtitle fw-regular">Streamline Your Projects</div>
                </div>
                <div className="common-body d-flex flex-column">
                    <h1 className="common-title fw-bold mb-3 text-center">Reset Your Password</h1>
                    <div className="reset-info">
                        <p>Create a new password for your account. Make sure it's strong and secure.</p>
                    </div>
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label htmlFor="newPassword">New Password</Form.Label>
                                    <div className="password-field d-flex position-relative">
                                        <Form.Control
                                            type={showPasswords.newPassword ? 'text' : 'password'}
                                            id="newPassword"
                                            name="newPassword"
                                            placeholder="Enter your new password"
                                            value={formData.newPassword}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            className="password-toggle bg-transparent ms-2"
                                            onClick={() => togglePassword('newPassword')}
                                            aria-label="Toggle password visibility"
                                        >
                                            <i className={`fas ${showPasswords.newPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </Button>
                                    </div>
                                    <div className="password-strength mt-2">
                                        <div className="strength-bar">
                                            <div
                                                className={`strength-fill ${passwordStrength.strengthClass}`}
                                                style={{ width: `${passwordStrength.score}%` }}
                                            ></div>
                                        </div>
                                        <small>{passwordStrength.text}</small>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label htmlFor="confirmPassword">Confirm New Password</Form.Label>
                                    <div className="password-field d-flex position-relative">
                                        <Form.Control
                                            type={showPasswords.confirmPassword ? 'text' : 'password'}
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            placeholder="Confirm your new password"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            className="password-toggle bg-transparent ms-2"
                                            onClick={() => togglePassword('confirmPassword')}
                                            aria-label="Toggle password visibility"
                                        >
                                            <i className={`fas ${showPasswords.confirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xs={12}>
                                <div>
                                    <div className={`requirement ${passwordStrength.requirements.length ? 'valid' : ''}`}>
                                        <i className="fas fa-circle"></i>
                                        <span>At least 8 characters</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.requirements.uppercase ? 'valid' : ''}`}>
                                        <i className="fas fa-circle"></i>
                                        <span>One uppercase letter</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.requirements.lowercase ? 'valid' : ''}`}>
                                        <i className="fas fa-circle"></i>
                                        <span>One lowercase letter</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.requirements.number ? 'valid' : ''}`}>
                                        <i className="fas fa-circle"></i>
                                        <span>One number</span>
                                    </div>
                                    <div className={`requirement ${passwordStrength.requirements.special ? 'valid' : ''}`}>
                                        <i className="fas fa-circle"></i>
                                        <span>One special character</span>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <Button type="submit" variant="primary" className="w-100 mb-4"> Reset Password </Button>
                            </Col>
                        </Row>
                    </Form>
                    <div className="text-center">
                        <Link to="/login" className='btn btn-link text-decoration-none'>Back to Sign In</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
